import {ReactElement, useContext, useEffect, useCallback, useState, useRef
} from "react";
import {useParams, useNavigate, Link} from "react-router-dom";
import {getSalesOrderEOURL} from "./links"
import {AllDataContext} from "./LoggedinMainPage"
import {ISODate} from "./date"
import utils from "./utils"
import {SOToolResult, getSOToolResults, SOToolLineResult} from "./sotoolengine"
import {NEVER} from "./constants"
import {SalesOrderLine} from "./api/salesorder"
import {ItemWarehouse} from "./api/itemwarehouse"
import "./SOTool.scss"
import {AllDataDone} from "./alldata"
import {WarehouseCode, ItemCode} from "./api/types"

const INF = "\u221e"

function dryRunSOTool(
  warehouseCode: WarehouseCode,
  allData: AllDataDone,
  firstPossibleDeliveryDate: ISODate
) {
  const soToolResults = getSOToolResults(
    warehouseCode, allData, firstPossibleDeliveryDate)
  soToolResults.forEach(sotr => sotr.lines.forEach(sotlr => {
    ;(sotlr.salesOrderLine as any).deliveryDate = sotlr.deliveryDate
  }))
  allData.tick()
}

function dateString(date: ISODate): string {
  return date.ge(NEVER) ? "never" : date.toDDMMYYYYString()
}

function delayDays(date1: ISODate, date2: ISODate): string {
  return date1.ge(NEVER) ? "-"
    : date2.ge(NEVER) ? INF
    : date1.getDaysUntil(date2).toString()
}

function show(
  func: (prePost: "pre" | "post") => number | string,
): ReactElement {
  const pre =  func("pre")
  const post = func("post")
  if (pre === post) {
    return <span className="unchanged">{pre}</span>
  }
  return <>
    <span className="pre">{pre}</span> <span className="post">{post}</span>
  </>
}

function newSetWithTioggle<T>(set: Set<T>, item: T): Set<T> {
  const newSet = new Set([...set])
  if (newSet.has(item)) {
    newSet.delete(item)
  } else {
    newSet.add(item)
  }
  return newSet
}

const TODAY = ISODate.utcToday()

export default function SOTool(): ReactElement {
  const [openLines, setOpenLines] = useState(new Set<number>())
  const [updating, setUpdating] = useState<SalesOrderLine>()
  const [updated, setUpdated] = useState<ReadonlySet<SalesOrderLine>>(new Set())
  const [error, setError] = useState<string>()
  const componentIsMounted = useRef<boolean>(false)
  const allData = useContext(AllDataContext)
  const warehouseCode = useParams().warehouseCode ?? ""
  const firstPossibleDeliveryDate =
    ISODate.parseISO(useParams().firstDate ?? TODAY.toISOString())
  const {division, warehouses} = allData
  const navigate = useNavigate()

  function sumOverSOToolResults(
    func: (sotr: SOToolResult) => number | ReadonlyArray<number>
  ): number {
    return soToolResults.flatMap(func).reduce((a, b) => a + b, 0)
  }
  const doNavigate = useCallback(
    ({newWarehouseCode, newFirstPossibleDeliveryDate, replace}:
      {newWarehouseCode?: string,
        newFirstPossibleDeliveryDate?: ISODate, replace?: boolean}) => {
      navigate([
        "",
        "division",
        division,
        "SOTool",
        "warehouse",
        newWarehouseCode ?? warehouseCode,
        (newFirstPossibleDeliveryDate ?? firstPossibleDeliveryDate).toISOString()
      ].map(encodeURIComponent).join("/"), {replace: replace ?? false})
    }, [navigate, division, warehouseCode, firstPossibleDeliveryDate])

  useEffect(() => {
    window.setTimeout(() => setUpdated(new Set()), 0)
  }, [firstPossibleDeliveryDate, warehouseCode, allData.division])

  useEffect(() => {
    componentIsMounted.current = true
    return () => {
      componentIsMounted.current = false
    }
  }, [])

  const warehouse = warehouses.find(w => w.code === warehouseCode)
  useEffect(function selectMainWarehouseIfNoneSelected() {
    if (warehouse) {
      return
    }
    const mainWarehouse = warehouses.find(w => w.main === 1) ?? warehouses[0]
    if (mainWarehouse) {
      doNavigate({newWarehouseCode: mainWarehouse.code, replace: true})
    }
  }, [doNavigate, division, warehouse, warehouses])

  if (!warehouse) {
    return <div>redirecting: {warehouseCode}</div>
  }
  if (error) {
    return <div className="error">
      <p>
        {error}
      </p>
      <button onClick={() => setError(undefined)}>
        Ignore error and continue (NOT ADVISED, AT YOUR OWN RISK)
      </button>
    </div>
  }
  const soToolResults = getSOToolResults(
    warehouse.code, allData, firstPossibleDeliveryDate)
  const postDelayWarnings = soToolResults
    .filter(sotr => sotr.getOrderDelayDays("post") > 0
      && sotr.salesOrder.$calc.warnIfDelayed)
  const postNeverWarnings = [...new Set(soToolResults
    .filter(sotr => sotr.salesOrder.deliveryDate.lt(NEVER))
    .flatMap(sotr => sotr.lines)
    .filter(sotlr => sotlr.deliveryDate.ge(NEVER))
    .map(sotlr => sotlr.salesOrderLine.itemCode))].sort()

  function getItemCodeLink(
    salesOrderLineOrItemCode: SalesOrderLine | ItemCode
  ): ReactElement {
    const itemCode: ItemCode = salesOrderLineOrItemCode instanceof SalesOrderLine
      ? salesOrderLineOrItemCode.itemCode : salesOrderLineOrItemCode
    const item = allData.items.find(item => item.code === itemCode)
    const description = (
      !(salesOrderLineOrItemCode instanceof SalesOrderLine)
      || salesOrderLineOrItemCode.description === null
      || salesOrderLineOrItemCode.description === ""
      || salesOrderLineOrItemCode.description === item?.description)
      ? <>{item?.description}</>
      : <>{item?.description} &mdash; {salesOrderLineOrItemCode.description}</>

    return <Link to={[
      "",
      "division",
      division,
      "PASDetailed",
      warehouseCode,
      itemCode,
      "0" + ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL.toString(),
      "0" + ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL.toString(),
    ].join("/")}>{itemCode} ({description})</Link>
  }

  const soToolResultsToShow = soToolResults.filter(
    sotr => sotr.hasChanges() || sotr.lines.some(
      sotlr => updated.has(sotlr.salesOrderLine)))

  async function updateOne(
    sotlr: SOToolLineResult,
  ): Promise<boolean> {
    const sol = sotlr.salesOrderLine
    console.log(`Updating SO:${sol.parent.orderNumber}(${sol.lineNumber}) `
      + `from ${sol.deliveryDate.toISOString()} `
      + `to ${sotlr.deliveryDate.toISOString()})`)
    if (!componentIsMounted.current) {
      console.log("Not mounted, not executing update")
      return false
    }
    setUpdating(sol)
    try{
      await sol.update({deliveryDate: sotlr.deliveryDate}, allData.division,
        "SOtool", allData.tick)
    } catch {
      setError("An error occurred while updating. It's advised to reload the page "
        + "and try again with the remaining items")
      return false
    }
    if (!componentIsMounted.current) {
      console.log("Not mounted, not finishing executing update")
      return false
    }
    setUpdating(undefined)
    setUpdated(updated => new Set([...updated, sol]))
    return true
  }

  async function updateAll() {
    for (const sotr of soToolResultsToShow) {
      for (const sotlr of sotr.lines.filter(sotlr => sotlr.hasChanges())) {
        const updateSuccessful = await updateOne(sotlr)
        if (!updateSuccessful) {
          console.log("terminating updateAll since last update not successful")
          return
        }
      }
    }
    console.log("Done updating all lines")
  }

  return <div className="SOTool">
    <div className="warehouseSelector">
      <span>Select a warehouse: </span>
      <select disabled={updating !== undefined}
        data-cy="select-warehouse" value={warehouseCode} onChange={
        e => doNavigate({newWarehouseCode: e.target.value})}>
        {[...warehouses].sort((a, b) => a.code.localeCompare(b.code)).map(
          warehouse => <option key={warehouse.code} value={warehouse.code}>
            {warehouse.code} - {warehouse.description}
          </option>
        )}
      </select>
    </div>
    <div className="firstPossibleDeliveryDateSelector">
      <span>Select first possible delivery date: </span>
      <select disabled={updating !== undefined}
        data-cy="select-first-possible-delivery-date"
        value={firstPossibleDeliveryDate.toISOString()} onChange={
          e => doNavigate({newFirstPossibleDeliveryDate:
            ISODate.parseISO(e.target.value)})}>
        {utils.range(-1, 7).map(offset => {
          const date = TODAY.add(offset)
          return <option key={date.toISOString()} value={date.toISOString()}>
            {date.weekDayName()} {date.date} {date.monthName()}
          </option>
        })
        }
      </select>
    </div>
    {postDelayWarnings.length > 0 && <div className="warnings">
      <p>
        Warning: After running the SO tool there
        {utils.isAreItems(postDelayWarnings.length, "order")} that have a delivery
        delay, even though the <code>&gt;@@warn-if-delayed</code> tag is set.
        It's advised to either change the delivery date for this order,
        or lower the quantities.
      </p>
      {postDelayWarnings.map(sotr => <div key={sotr.salesOrder.orderNumber}>
        <a href={getSalesOrderEOURL(division, sotr.salesOrder.orderID).toString()}
          target="_blank" rel="noreferrer">
          Sales order {sotr.salesOrder.orderNumber}
        </a> for {sotr.salesOrder.orderedByName} has requested delivery date {
          sotr.salesOrder.deliveryDate.toDDMMYYYYString()}, will have a delay of {
            sotr.getOrderDelayDays("post")} days:
        <ul>
          {sotr.lines.filter(sotlr =>
            sotlr.availableStockOnRequestedDate < sotlr.salesOrderLine.quantity)
            .map(sotlr => <li key={sotlr.salesOrderLine.iD}>
              Line {sotlr.salesOrderLine.lineNumber}:
              {" "} {sotlr.salesOrderLine.quantity}x
              {" "} {getItemCodeLink(sotlr.salesOrderLine)}
              {" "} ({sotlr.salesOrderLine.itemDescription})
              {" "} will get delivery date {sotlr.deliveryDate.toDDMMYYYYString()
              }. On requested date there is an available stock of {
                sotlr.availableStockOnRequestedDate}
            </li>)}
          {sotr.salesOrder.deliveryDate.lt(firstPossibleDeliveryDate) &&
            <li>The requested date is before the "first possible delivery date"</li>
          }
        </ul>
      </div>)}
    </div>
    }
    {postNeverWarnings.length > 0 && <div className="warnings">
      <p>
        Warning: Some items have more sales orders than projected stock.
        Therefore the SO tool can not put a sensible deliver date on these order
        lines. They will be shown as "never" in the overview below, and
        as {NEVER.toDDMMYYYYString()} in Exact Online. This is the case for
        (some of the orders of) the following products:
      </p>
      <ul>
        {postNeverWarnings.map(itemCode => <li key={itemCode}>
          {getItemCodeLink(itemCode)}
        </li>)}
      </ul>
    </div>
    }
    <div>
      <button onClick={() => dryRunSOTool(
        warehouse.code, allData, firstPossibleDeliveryDate)}>Dry run</button>
      <button className="live"
        disabled={soToolResultsToShow.length === 0 || updating !== undefined}
        onClick={() => updateAll()}
      >
        Update all in Exact Online
      </button>
    </div>

    <table className="summary">
      <tbody>
        <tr>
          <th>
            Number of lines that have delivery date "never"
            (where requested date is not "never")
          </th>
          <td>
            {show(prePost => {
              const unfulfilled = soToolResults
              .flatMap(sotr => sotr.lines)
              .filter(sotlr => sotlr.getDeliveryDate(prePost).ge(NEVER))
              const becauseOfStock = unfulfilled.filter(
                sotlr => sotlr.salesOrderLine.parent.deliveryDate.lt(NEVER))
              return `${unfulfilled.length} (${becauseOfStock.length})`
            })}
          </td>
        </tr>
        <tr>
          <th>Number of shipments</th>
          <td>
            {show(prePost => sumOverSOToolResults(
              sotr => sotr.getNumberOfPartialShipments(prePost)))}
          </td>
        </tr>
        <tr>
          <th>Total number of non-completed orders (number shown)</th>
          <td>{soToolResults.length} ({soToolResultsToShow.length})</td>
        </tr>
        <tr>
          <th>Total number of non-completed order lines (number shown)</th>
          <td>
            {soToolResults.flatMap(sotr => sotr.lines).length} (
            {soToolResults.flatMap(sotr => sotr.lines.filter(
              sotlr => sotlr.hasChanges() || updated.has(sotlr.salesOrderLine))
            ).length})
          </td>
        </tr>
      </tbody>
    </table>

    <table className={`resulttable`}>
      <thead>
        <tr className="sales-order-row">
          <th> Order nr </th>
          <th> Priority </th>
          <th> Customer </th>
          <th> Requested date </th>
          <th> Nr shipments </th>
          <th> Delay-days </th>
        </tr>
        <tr>
          <th> line nr </th>
          <th> nr items </th>
          <th> item </th>
          <th colSpan={2}> Delivery date </th>
          <th>Delay-days</th>
        </tr>
      </thead>
      {soToolResultsToShow.map(
        sotr => <tbody key={sotr.salesOrder.orderNumber} className={
          openLines.has(sotr.salesOrder.orderNumber) ? "open" : "closed"}>
          <tr className="sales-order-row">
            <td>
              <a href={getSalesOrderEOURL(division, sotr.salesOrder.orderID)
                .toString()} target="_blank" rel="noreferrer">
                {sotr.salesOrder.orderNumber}
              </a>
            </td>
            <td>{sotr.salesOrder.$calc.priority}</td>
            <td>{sotr.salesOrder.orderedByName}</td>
            <td>{dateString(sotr.salesOrder.deliveryDate)}</td>
            <td>{show(
              prePost =>sotr.getNumberOfPartialShipments(prePost))}</td>
            <td>{show(prePost => {
              const date = sotr.getEverythingDeliveredDate(prePost)
              return delayDays(sotr.salesOrder.deliveryDate, date)
              })}</td>
          </tr>
          {sotr.salesOrder.salesOrderLines.map(sol => {
            const sotlr = sotr.lines.find(sotlr => sotlr.salesOrderLine === sol)
            const delivered = sotlr === undefined
            return <tr key={sol.iD}
              className={[
                "line",
                ...(delivered ? ["delivered"] : [
                "notdelivered",
                sotlr.hasChanges() ? "changed" : (
                updated.has(sotlr.salesOrderLine) ? "updated" : "notchanged"),
                sol.deliveryDate !== sotr.salesOrder.deliveryDate
                  ? "preDelayed" : "notPreDelayed",
                sotlr.deliveryDate !== sotr.salesOrder.deliveryDate
                  ? "postDelayed" : "notPostDelayed",
                ])
              ].join(" ")} >
              <td>{sol.lineNumber}</td>
              <td>
                {sol.quantity}
              </td>
              <td>{getItemCodeLink(sol)}</td>
              <td colSpan={2}>
                {delivered ? <>delivered</>:<>
                  {show(prePost => {
                    const date = prePost === "pre"
                      ? sol.deliveryDate : sotlr.deliveryDate
                    return dateString(date)
                  })}
                </>
                }
                {sotlr && sotlr.hasChanges() &&
                  <button className="update-delivery-date live"
                    disabled={updating !== undefined}
                    onClick={() => updateOne(sotlr)}
                  >{updating === sol ? "updating..." : "update"}</button>
                }
              </td>
              <td>
                {show(prePost => {
                  const [date, combineShipmentDelay] =
                    (delivered || prePost === "pre")
                      ? [sol.deliveryDate, null]
                      : [sotlr.deliveryDate,
                    sotlr.delayDaysBecauseOfNonPartialShipment]
                  return delayDays(sol.parent.deliveryDate, date) + (
                  combineShipmentDelay ? `(${combineShipmentDelay})` : "")
                })}
              </td>
            </tr>})}
          {sotr.lines.filter(
              sotlr => !sotlr.hasChanges()&& !updated.has(sotlr.salesOrderLine)
              ).length > 0 && <tr>
            <td className="showUnchanged" colSpan={7}>
              <button onClick={() => setOpenLines(
                s => newSetWithTioggle(s, sotr.salesOrder.orderNumber))}>
                {sotr.salesOrder.salesOrderLines.length -
                  sotr.lines.filter(sotlr => sotlr.hasChanges() ||
                  updated.has(sotlr.salesOrderLine)).length}
              </button>
            </td>
          </tr>}
        </tbody>
      )}
    </table>
    {soToolResultsToShow.length === 0 && <div className="allDone">
      Congratulations, the Sales Order tool has no suggestions for any changes
      &#x1F604;
    </div>}
  </div>
}
